import React, { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { pixelRolled, setPixelConnectionStatus, addPixel } from '../actions/pixels';
import { testPixelConnection, PIXEL_COLORS } from '../lib/pixellib';
import { isDev } from '../lib/utils';
import { repeatConnect, requestPixel } from '@systemic-games/pixels-web-connect';
import { usePixelManager } from '../contexts/PixelContext';

const PixelManager = () => {
  const dispatch = useDispatch();
  const pixels = useSelector(state => Object.values(state.pixels.list));
  const game = useSelector(state => state.game);
  const characterId = useSelector(state => state.characterId);
  const { setPixelManager } = usePixelManager();

  // Store pixel instances and their event listeners
  const pixelInstancesRef = useRef(new Map());
  const testingRef = useRef(false);

  // Store latest state in refs for use in callbacks
  const latestStateRef = useRef({ pixels: [], game: null, characterId: null });

  // Get CSRF token once and reuse it
  const csrfToken = React.useMemo(() => {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }, []);

  // Update refs when state changes
  useEffect(() => {
    latestStateRef.current = { pixels, game, characterId };
  }, [pixels, game, characterId]);

  // Manual connection test function (can be called from other components)
  const forceConnectionTest = useCallback(async () => {
    if (testingRef.current) return; // Prevent concurrent tests
    testingRef.current = true;
    if (isDev) console.log('[Pixel Debug] PixelManager: Starting manual connection test for', latestStateRef.current.pixels.length, 'pixels');

    const blinkColor = isDev ? PIXEL_COLORS.GREEN.pixel_constant : PIXEL_COLORS.BLACK.pixel_constant;

    await Promise.all(latestStateRef.current.pixels.map(async (pixel) => {
      const result = await testPixelConnection(pixel.pixel, blinkColor);
      if (isDev) console.log('[Pixel Debug] PixelManager: Connection test result for pixel', pixel.pixelId, ':', result);
      if (pixel.pixelId) {
        dispatch(setPixelConnectionStatus(pixel.pixelId, result));
      }
    }));

    if (isDev) console.log('[Pixel Debug] PixelManager: Manual connection test completed');
    testingRef.current = false;
  }, [dispatch]);

  // Create roll handler with current state
  const createRollHandler = useCallback((pixel, currentCharacterId, currentGameId) => (face) => {
    if (isDev) console.log('[Pixel Debug] PixelManager: Roll event received for pixel', pixel.pixelId, ':', face);

    // Dispatch Redux action
    dispatch(pixelRolled(pixel.pixelId, face));

    // Send roll data to backend using current state
    const currentState = latestStateRef.current;
    const pixelsFromStore = currentState.pixels;
    const pixelFromStore = pixelsFromStore.find(p => p.pixelId === pixel.pixelId);
    const characterIdForRoll = pixelFromStore ? pixelFromStore.characterId : currentCharacterId;

    fetch(`/pixels/roll/game/${currentGameId}/character/${characterIdForRoll}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken
      },
      body: JSON.stringify({
        timestamp: new Date().toISOString(),
        appVersion: pixel.appVersion,
        appBuild: pixel.appBuild,
        profileName: pixel.profileName,
        actionValue: pixel.actionValue,
        pixelName: pixel.name,
        pixelId: pixel.pixelId,
        faceValue: face,
        faceIndex: face - 1,
        ledCount: pixel.ledCount,
        colorway: pixel.colorway,
        dieType: pixel.dieType,
        firmwareTimestamp: pixel.firmwareTimestamp,
        rssi: pixel.rssi,
        batteryLevel: pixel.batteryLevel,
        isCharging: pixel.isCharging,
        rollState: pixel.rollState
      })
    }).then(response => {
      if (isDev) console.log('[Pixel Debug] PixelManager: Backend response:', response.status);
      if (!response.ok) {
        console.error(`[Pixel Debug] PixelManager: Backend returned an error status: ${response.status}`);
      }
    }).catch(error => {
      if (isDev) console.error('[Pixel Debug] PixelManager: Backend fetch error:', error);
    });
  }, [dispatch]);

  // Pair a new Pixel
  const pairPixel = useCallback(async (characterId) => {
    try {
      if (isDev) console.log('[Pixel Debug] PixelManager: Starting pairing process');

      const pixel = await requestPixel();
      await repeatConnect(pixel);

      // Clean up any existing event listener for this pixel ID before adding new one
      const existingInstance = pixelInstancesRef.current.get(pixel.pixelId);
      if (existingInstance) {
        if (isDev) console.log('[Pixel Debug] PixelManager: Cleaning up existing event listener for pixel:', pixel.pixelId);
        if (existingInstance.pixel && existingInstance.pixel.removeEventListener) {
          existingInstance.pixel.removeEventListener('roll', existingInstance.rollHandler);
        }
        pixelInstancesRef.current.delete(pixel.pixelId);
      }

      if (isDev) console.log('[Pixel Debug] PixelManager: Pixel paired successfully, setting up event listener');

      // Create roll event handler with current state
      const rollHandler = createRollHandler(pixel, characterId, game.id);

      // Note: We store the non-serializable pixel instance in Redux because it's essential
      // for hardware interaction (blink, roll events, etc.) and cannot be recreated from
      // serializable data alone. This breaks Redux time-travel debugging and persistence,
      // but is necessary for the core Pixel functionality.
      const pixelConfig = {
        systemId: pixel.systemId,
        pixelId: pixel.pixelId,
        dieType: pixel.dieType,
        name: pixel.name,
        lastRoll: pixel.currentFace,
        characterId: characterId,
        pixel: pixel,
        connectionStatus: true // Assume connected initially since we just paired
      };

      // Add event listener
      pixel.addEventListener('roll', rollHandler);

      // Store pixel instance and handler
      pixelInstancesRef.current.set(pixel.pixelId, {
        pixel,
        rollHandler
      });

      // Add to Redux store
      dispatch(addPixel(pixelConfig));

      // Blink to confirm pairing
      await pixel.blink(PIXEL_COLORS.GREEN.pixel_constant);

      if (isDev) console.log('[Pixel Debug] PixelManager: Running connection test after pairing');

      // Run connection test
      const result = await testPixelConnection(pixel, PIXEL_COLORS.BLACK.pixel_constant);
      if (pixel && pixel.pixelId) {
        dispatch(setPixelConnectionStatus(pixel.pixelId, result));
      }

      if (isDev) console.log('[Pixel Debug] PixelManager: Pairing completed, connection test result:', result);

    } catch (error) {
      if (error.name !== 'NotFoundError') {
        if (isDev) console.error('[Pixel Debug] PixelManager: Error pairing pixel:', error);
      }
    }
  }, [game.id, dispatch, createRollHandler]);

  // Register manager functions with context
  useEffect(() => {
    if (isDev) console.log('[Pixel Debug] PixelManager: Registering functions with context');
    setPixelManager({
      forceConnectionTest,
      pairPixel
    });
  }, [setPixelManager, forceConnectionTest, pairPixel]);

  // Once-per-minute keepalive connection test
  useEffect(() => {
    if (pixels.length === 0) {
      if (isDev) console.log('[Pixel Debug] PixelManager: Keepalive - no pixels, skipping interval setup');
      return;
    }

    if (isDev) console.log('[Pixel Debug] PixelManager: Keepalive - setting up interval for', pixels.length, 'pixels');

    const interval = setInterval(async () => {
      const currentPixels = latestStateRef.current.pixels;

      if (isDev) console.log('[Pixel Debug] PixelManager: Keepalive - testing', currentPixels.length, 'pixels');
      await Promise.all(currentPixels.map(async (pixel) => {
        const blinkColor = isDev ? PIXEL_COLORS.GREEN.pixel_constant : PIXEL_COLORS.BLACK.pixel_constant;
        let result = await testPixelConnection(pixel.pixel, blinkColor);
        if (isDev) console.log('[Pixel Debug] PixelManager: Keepalive - pixel', pixel.pixelId, 'result:', result);
        if (pixel.pixelId) {
          dispatch(setPixelConnectionStatus(pixel.pixelId, result));
        }
      }));
    }, 60000);

    if (isDev) console.log('[Pixel Debug] PixelManager: Keepalive - interval set, will run in 60 seconds');

    return () => {
      if (isDev) console.log('[Pixel Debug] PixelManager: Keepalive - clearing interval');
      clearInterval(interval);
    };
  }, [pixels.length, dispatch]);

  // Set up event listeners for all pixels
  useEffect(() => {
    if (isDev) console.log('[Pixel Debug] PixelManager: Syncing event listeners for', pixels.length, 'pixels');

    // Clean up all existing listeners before re-adding them
    pixelInstancesRef.current.forEach(({ pixel, rollHandler }) => {
      if (pixel && typeof pixel.removeEventListener === 'function') {
        pixel.removeEventListener('roll', rollHandler);
      }
    });
    pixelInstancesRef.current.clear();

    // Set up listeners for all current pixels
    pixels.forEach(pixel => {
      if (pixel.pixel && typeof pixel.pixel.addEventListener === 'function') {
        const rollHandler = createRollHandler(pixel.pixel, characterId, game.id);
        pixel.pixel.addEventListener('roll', rollHandler);
        pixelInstancesRef.current.set(pixel.pixelId, {
          pixel: pixel.pixel,
          rollHandler
        });
      }
    });

    if (isDev) console.log('[Pixel Debug] PixelManager: Synced event listeners:', Array.from(pixelInstancesRef.current.keys()));
  }, [pixels.length, game.id, characterId, createRollHandler]);

  // Cleanup event listeners when component unmounts
  useEffect(() => {
    return () => {
      if (isDev) console.log('[Pixel Debug] PixelManager: Cleaning up event listeners');
      pixelInstancesRef.current.forEach(({ pixel, rollHandler }) => {
        if (pixel && pixel.removeEventListener) {
          pixel.removeEventListener('roll', rollHandler);
        }
      });
      pixelInstancesRef.current.clear();
    };
  }, []);

  // This component doesn't render anything - it just manages Pixel events
  return null;
};

export default PixelManager;